import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { LocalAuthorizationList } from './chargepoints-api.service';

const HOST = environment.serverUrl;
const CHARGEPOINTS_API_URL = HOST + '/ocpp/chargePoints';


export interface ChargepointConfigurationUpdate {
  key: string;
  value: string;
}

export interface ChargepointConfiguration extends ChargepointConfigurationUpdate {
  readonly: boolean;
}

export interface OcppSession {
  sessionId: string;
  chargePointId: string;
  closed: boolean;
  creationTime: Date;
  closeTime?: Date;
}

function reviveSession(session: OcppSession): OcppSession {
  session.creationTime = session.creationTime ? new Date(session.creationTime) : new Date(0);
  session.closeTime = session.closeTime ? new Date(session.closeTime) : undefined;
  return session;
}

@Injectable({ providedIn: 'root' })
export class ChargepointsOcppApiService {

  constructor(private http: HttpClient) { }

  public getChargepointConfiguration(chargepointId: string): Observable<ChargepointConfiguration[]> {
    return this.http.get<ChargepointConfiguration[]>(`${CHARGEPOINTS_API_URL}/${chargepointId}/configuration`);
  }

  public getChargepointSessions(chargepointId: string, showFrom: Date, showTo: Date): Observable<OcppSession[]> {
    return this.http.get<OcppSession[]>(`${CHARGEPOINTS_API_URL}/${chargepointId}/sessions?from=${showFrom.toISOString()}&to=${showTo.toISOString()}`).pipe(
      map(sessions => sessions.map(reviveSession))
    );
  }

  public updateChargepointConfiguration(chargepointId: string, configuration: ChargepointConfigurationUpdate): Observable<string> {
    return this.http.post<string>(`${CHARGEPOINTS_API_URL}/${chargepointId}/configuration`, configuration);
  }

  public unlockConnector(chargepointId: string, connectorId: number): Observable<string> {
    return this.http.post<string>(`${CHARGEPOINTS_API_URL}/${chargepointId}/connectors/${connectorId}/unlock`, {});
  }

  public requestConnectorStatus(chargepointId: string, connectorId: number): Observable<string> {
    return this.http.post<string>(`${CHARGEPOINTS_API_URL}/${chargepointId}/connectors/${connectorId}/status`, {});
  }

  public requestDiagnostic(chargepointId: string): Observable<any> {
    return this.http.post<any>(`${CHARGEPOINTS_API_URL}/${chargepointId}/diagnostic`, {});
  }

  public clearCache(chargepointId: string): Observable<any> {
    return this.http.post<any>(`${CHARGEPOINTS_API_URL}/${chargepointId}/clearCache`, {});
  }

  public reboot(chargepointId: string, hard: boolean): Observable<any> {
    return this.http.post<any>(`${CHARGEPOINTS_API_URL}/${chargepointId}/reboot`, {forced: hard});
  }

  public changeAvailability(chargepointId: string, operative: boolean): Observable<any> {
    return this.http.post<any>(`${CHARGEPOINTS_API_URL}/${chargepointId}/availability`, {operative});
  }

  public addLocalAuthorizationListIdentifier(chargepointId: string, identifier: string): Observable<LocalAuthorizationList> {
    return this.http.post<LocalAuthorizationList>(
      `${CHARGEPOINTS_API_URL}/${chargepointId}/localAuthorizationList/identifiers`,
      { identifier }
    );
  }

  public removeLocalAuthorizationListIdentifier(chargepointId: string, identifier: string): Observable<LocalAuthorizationList> {
    return this.http.delete<LocalAuthorizationList>(
      `${CHARGEPOINTS_API_URL}/${chargepointId}/localAuthorizationList/identifiers/${encodeURIComponent(identifier)}`
    );
  }

  public setLocalAuthorizationListEnabled(chargepointId: string, enabled: boolean): Observable<LocalAuthorizationList> {
    return this.http.post<LocalAuthorizationList>(
      `${CHARGEPOINTS_API_URL}/${chargepointId}/localAuthorizationList/enabled`,
      { enabled }
    );
  }

  public synchronizeLocalAuthorizationList(chargepointId: string): Observable<LocalAuthorizationList> {
    return this.http.post<LocalAuthorizationList>(
      `${CHARGEPOINTS_API_URL}/${chargepointId}/localAuthorizationList/synchronize`,
      {}
    );
  }
}
